import i18n from '@/i18n'
import { getNumericMatrixColumns } from '@/lib/utils/aggregation'

/**
 * The question-type vocabulary the run gate already shows
 * (`threads.detail.gate.types.*`), reused so the report names a question the
 * same way the builder did. Resolved from the SurveyJS element shape alone:
 * no survey, question id or answer value is consulted.
 */
export type ReportQuestionKind =
  | 'rating'
  | 'single_select'
  | 'multi_select'
  | 'ranking'
  | 'continuous'
  | 'open_end'
  | 'matrix_rating'
  | 'matrix_categorical'

export interface ReportQuestionKindHints {
  /** A `text` question whose answers the card charts as categories. */
  isCategoricalText?: boolean
  /** A `text` question whose answers the card charts as numbers. */
  isNumericText?: boolean
}

function isNumericScale(columns: Array<any>): boolean {
  return (
    columns.length > 0 &&
    columns.every((column) => Number.isFinite(Number(column?.value ?? column)))
  )
}

function isTextCell(column: any): boolean {
  const cellType = String(column?.cellType || column?.inputType || '').toLowerCase()
  return cellType === 'text' || cellType === 'comment'
}

export function resolveReportQuestionKind(
  question: any,
  hints: ReportQuestionKindHints = {},
): ReportQuestionKind | null {
  const columns: Array<any> = Array.isArray(question?.columns) ? question.columns : []

  switch (question?.type) {
    case 'radiogroup':
    case 'dropdown':
    case 'boolean':
      return 'single_select'
    case 'checkbox':
      return 'multi_select'
    case 'rating':
      return 'rating'
    case 'ranking':
      return 'ranking'
    case 'comment':
      return 'open_end'
    case 'text': {
      if (question.inputType === 'number' || hints.isNumericText) {
        return 'continuous'
      }
      return hints.isCategoricalText ? 'single_select' : 'open_end'
    }
    case 'matrix':
      return isNumericScale(columns) ? 'matrix_rating' : 'matrix_categorical'
    case 'matrixdropdown':
    case 'matrixdynamic': {
      if (columns.length > 0 && columns.every(isTextCell)) {
        return 'open_end'
      }
      return getNumericMatrixColumns(question).length > 0 ? 'matrix_rating' : 'matrix_categorical'
    }
    default:
      return null
  }
}

export function reportQuestionKindLabel(kind: ReportQuestionKind): string {
  return i18n.t(`threads.detail.gate.types.${kind}`, { ns: 'app' })
}
